import React, { useEffect, useRef, useState } from 'react';
import { TonConnectButton, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { Address } from '@ton/core';
import { retrieveLaunchParams } from '@telegram-apps/sdk';
import {
  useGameStore,
  useGameSelectors,
  useAutoRefresh,
  useCountdown,
  formatTimeLeft,
  formatGram,
  shortAddress,
} from '../stores/gameStore';
import { useDeposit } from '../hooks/useDeposit';
import { t, plural, lang, setLang, LANG_NAMES, type Lang } from '../i18n';
import { API_ENDPOINTS, API_URL, BOT_LINK, CONTRACT_ADDRESS, DEPOSIT_AMOUNT, LAUNCH_AT, NETWORK, ROUND_DURATION, WS_URL } from '../config';
import type { DepositItem, HistoryItem, LeaderboardItem } from '../types';
import BlockiesIdenticon from './BlockiesIdenticon';
import { IconGlobe, IconInfo, IconMusic, IconVolume, IconVolumeOff } from './icons';

const viewerBase = NETWORK === 'mainnet'
  ? 'https://tonviewer.com'
  : 'https://testnet.tonviewer.com';

function normalize(addr: string | null | undefined): string | null {
  if (!addr) return null;
  try {
    return Address.parse(addr).toString();
  } catch {
    return addr;
  }
}

interface LockState {
  unlock_time: number;
  locked: boolean;
  last_invite_time: number;
}

// Referrer from Mini App start_param (ref_<address>) or ?startapp= in browser
function readReferrer(): string | null {
  let raw = new URLSearchParams(window.location.search).get('startapp') ?? '';
  if (!raw) {
    try {
      // Telegram passes launch params in location.hash
      raw = retrieveLaunchParams().tgWebAppStartParam ?? '';
    } catch {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      raw = hash.get('tgWebAppStartParam') ?? '';
    }
  }
  const m = raw.match(/^ref_(\S+)$/);
  return m ? m[1] : null;
}

// ?demo=<seconds> renders the locked state without a wallet (design preview)
const DEMO_LOCK = Number(new URLSearchParams(window.location.search).get('demo') || 0);

const BUILTIN_SOUNDS = [
  { id: 'ding', name: 'Ding' },
  { id: 'coin', name: 'Coin' },
  { id: 'chime', name: 'Chime' },
  { id: 'pluck', name: 'Pluck' },
  { id: 'pop', name: 'Pop' },
];
const DEFAULT_SOUND = `/sounds/${BUILTIN_SOUNDS[0].id}.wav`;

// Persisted deposit-sound file (IndexedDB — localStorage can't hold blobs)
const soundStore = {
  open(): Promise<IDBDatabase> {
    return new Promise((res, rej) => {
      const r = indexedDB.open('kg', 1);
      r.onupgradeneeded = () => { r.result.createObjectStore('kv'); };
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
  },
  async get(key: string): Promise<Blob | undefined> {
    const db = await this.open();
    return new Promise((res, rej) => {
      const rq = db.transaction('kv').objectStore('kv').get(key);
      rq.onsuccess = () => res(rq.result as Blob | undefined);
      rq.onerror = () => rej(rq.error);
    });
  },
  async put(key: string, val: Blob): Promise<void> {
    const db = await this.open();
    return new Promise((res, rej) => {
      const rq = db.transaction('kv', 'readwrite').objectStore('kv').put(val, key);
      rq.onsuccess = () => res();
      rq.onerror = () => rej(rq.error);
    });
  },
};

const Game: React.FC = () => {
  const wallet = useTonWallet();
  const {
    round,
    timeLeft,
    isPaused,
    isLoading,
    error,
    canDeposit,
    currentJackpot,
    formattedTimeLeft,
    roundNumber,
    depositCount,
  } = useGameSelectors();
  const { sendDeposit, isLoading: depositing } = useDeposit();
  const [tonConnectUI] = useTonConnectUI();

  useAutoRefresh();
  useCountdown();

  const [deposits, setDeposits] = useState<DepositItem[]>([]);
  const [lastWinner, setLastWinner] = useState<HistoryItem | null>(null);
  const [nextRoundTime, setNextRoundTime] = useState(0);
  const [depositError, setDepositError] = useState<string | null>(null);
  const [lock, setLock] = useState<LockState | null>(null);
  const [lockLeft, setLockLeft] = useState(0);
  const [invited, setInvited] = useState(false);
  const [winnersOpen, setWinnersOpen] = useState(false);
  const [winners, setWinners] = useState<HistoryItem[]>([]);
  const [depositsOpen, setDepositsOpen] = useState(false);
  const [allDeposits, setAllDeposits] = useState<DepositItem[]>([]);
  const [referrer] = useState<string | null>(readReferrer);
  const [leaders, setLeaders] = useState<LeaderboardItem[]>([]);
  const [overlayTab, setOverlayTab] = useState<'winners' | 'leaders'>('winners');
  const [langOpen, setLangOpen] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem('kg_sound') !== '0'; } catch { return true; }
  });
  // null while a custom blob is still loading — falls back to the default builtin
  const [soundUrl, setSoundUrl] = useState<string | null>(() => {
    try {
      const src = localStorage.getItem('kg_sound_src');
      if (src && src !== 'custom') return src;
    } catch {}
    return DEFAULT_SOUND;
  });
  const [customUrl, setCustomUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const seenTxRef = useRef<Set<string>>(new Set());
  const firstFetchRef = useRef(true);
  const soundRef = useRef<{ on: boolean; url: string | null }>({ on: true, url: null });
  soundRef.current = { on: soundOn, url: soundUrl };

  useEffect(() => {
    // A custom picked file only wins when the user last selected it.
    try {
      if (localStorage.getItem('kg_sound_src') !== 'custom') return;
    } catch { return; }
    soundStore.get('deposit_sound')
      .then((blob) => { if (blob) { const u = URL.createObjectURL(blob); setCustomUrl(u); setSoundUrl(u); } })
      .catch(() => {});
  }, []);

  const playDepositSound = () => {
    const { on, url } = soundRef.current;
    if (on && url) new Audio(url).play().catch(() => {});
  };

  const onSoundClick = () => {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem('kg_sound', next ? '1' : '0'); } catch {}
    const url = soundRef.current.url;
    if (next && url) new Audio(url).play().catch(() => {});
  };

  const pickBuiltin = (id: string) => {
    const url = `/sounds/${id}.wav`;
    setSoundUrl(url);
    try { localStorage.setItem('kg_sound_src', url); } catch {}
    new Audio(url).play().catch(() => {});
  };

  const onSoundFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    soundStore.put('deposit_sound', f).catch(() => {});
    setCustomUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      const u = URL.createObjectURL(f);
      setSoundUrl(u);
      new Audio(u).play().catch(() => {});
      return u;
    });
    setSoundOn(true);
    try {
      localStorage.setItem('kg_sound', '1');
      localStorage.setItem('kg_sound_src', 'custom');
    } catch {}
    e.target.value = '';
  };
  const [rulesOpen, setRulesOpen] = useState(() => {
    try { return !localStorage.getItem('kg_rules_seen'); } catch { return true; }
  });
  const [launchLeft, setLaunchLeft] = useState(() => LAUNCH_AT - Date.now() / 1000);
  const preLaunch = launchLeft > 0;

  useEffect(() => {
    if (!preLaunch) return;
    const tick = () => setLaunchLeft(LAUNCH_AT - Date.now() / 1000);
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [preLaunch]);

  const closeRules = () => {
    setRulesOpen(false);
    try { localStorage.setItem('kg_rules_seen', '1'); } catch {}
  };

  const setIfChanged = <T,>(prev: T, next: T): T =>
    JSON.stringify(prev) === JSON.stringify(next) ? prev : next;

  const fetchSideData = () => {
    fetch(API_ENDPOINTS.DEPOSITS)
      .then((r) => (r.ok ? r.json() : []))
      .then((deps: DepositItem[]) => {
        const seen = seenTxRef.current;
        const fresh = deps.filter(
          (d) => !seen.has(d.tx_hash ?? `${d.player_address}|${d.deposit_time}`),
        );
        deps.forEach((d) => seen.add(d.tx_hash ?? `${d.player_address}|${d.deposit_time}`));
        if (!firstFetchRef.current && fresh.length > 0) playDepositSound();
        firstFetchRef.current = false;
        setDeposits((prev) => setIfChanged(prev, deps));
      })
      .catch(() => {});
    fetch(`${API_ENDPOINTS.HISTORY}?limit=1`)
      .then((r) => (r.ok ? r.json() : []))
      .then((h: HistoryItem[]) => setLastWinner((prev) => setIfChanged(prev, h[0] ?? null)))
      .catch(() => {});
    fetch(`${API_ENDPOINTS.LEADERBOARD}?limit=10`)
      .then((r) => (r.ok ? r.json() : []))
      .then((l: LeaderboardItem[]) => setLeaders((prev) => setIfChanged(prev, l)))
      .catch(() => {});
  };

  useEffect(() => {
    fetchSideData();
    const id = setInterval(fetchSideData, 15000);
    return () => clearInterval(id);
  }, [roundNumber]);

  // Live push: the watcher streams round state over WS; polling stays as fallback.
  useEffect(() => {
    let ws: WebSocket | null = null;
    let closed = false;
    let retry = 0;
    const connect = () => {
      ws = new WebSocket(WS_URL);
      ws.onopen = () => { retry = 0; };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string);
          if (msg?.type === 'round' && msg.data) {
            useGameStore.getState().applyRoundData(msg.data);
            fetchSideData();
          }
        } catch { /* ignore malformed frames */ }
      };
      ws.onclose = () => {
        if (closed) return;
        retry += 1;
        setTimeout(connect, Math.min(10_000, 1000 * retry));
      };
    };
    connect();
    return () => { closed = true; ws?.close(); };
  }, [roundNumber]);

  useEffect(() => {
    if (!isPaused || !round?.next_round_start) return;
    const tick = () =>
      setNextRoundTime(Math.max(0, round.next_round_start - Date.now() / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isPaused, round?.next_round_start]);

  const myAddress = wallet ? normalize(wallet.account.address) : null;

  // Personal lock: poll the contract getter for the connected wallet
  useEffect(() => {
    if (!myAddress) {
      setLock(null);
      return;
    }
    // Anonymous language ping — cumulative stats only.
    fetch(`${API_URL}/api/player_lang`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address: myAddress, lang }),
    }).catch(() => {});
    const fetchLock = () =>
      fetch(`${API_ENDPOINTS.LOCK}/${encodeURIComponent(myAddress)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then(setLock)
        .catch(() => {});
    fetchLock();
    const id = setInterval(fetchLock, 15000);
    return () => clearInterval(id);
  }, [myAddress, roundNumber]);

  // Local 1s tick for the personal countdown
  useEffect(() => {
    if (!lock?.locked) {
      setLockLeft(0);
      return;
    }
    const tick = () =>
      setLockLeft(Math.max(0, lock.unlock_time - Date.now() / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lock?.locked, lock?.unlock_time]);

  const locked = DEMO_LOCK > 0 || (!!lock?.locked && lockLeft > 0);
  const lockDisplay = DEMO_LOCK > 0 ? DEMO_LOCK : lockLeft;
  const inviteOnCooldown = !!lock && lock.last_invite_time > 0
    && Date.now() / 1000 - lock.last_invite_time < 1800;

  const handleDeposit = async () => {
    if (!wallet || !canDeposit || depositing || locked) return;
    setDepositError(null);
    const result = await sendDeposit(undefined, referrer ?? undefined);
    if (!result.success && result.error !== 'USER_CANCELLED') {
      setDepositError(result.error ?? 'Transaction failed');
    }
  };

  const inviteLink = `${BOT_LINK || 'https://t.me/'}?startapp=ref_${myAddress ?? ''}`;

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
    } catch {
      window.prompt(t('copy_prompt'), inviteLink);
    }
    setInvited(true);
  };

  const depositDisabled = !wallet || !canDeposit || depositing || locked || preLaunch;
  const progress = isPaused ? 0 : Math.min(100, (timeLeft / ROUND_DURATION) * 100);

  return (
    <div className="app">
      <header className="header">
        <div className="brand">
          <span className="brand-mark">◆</span>
          <span className="brand-name">kiloGRAM</span>
        </div>
        <TonConnectButton />
        <div className="header-icons">
          <span className="info-btn" onClick={onSoundClick}>
            {soundOn && soundUrl ? <IconVolume /> : <IconVolumeOff />}
          </span>
          <span className="info-btn" onClick={() => setSoundOpen(true)}><IconMusic /></span>
          <span className="info-btn" onClick={() => setLangOpen(true)}><IconGlobe /></span>
          <span className="info-btn" onClick={() => setRulesOpen(true)}><IconInfo /></span>
        </div>
      </header>

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={onSoundFile}
      />

      <main className="main">
        <section className="card jackpot-card">
          <div className="card-label">{t('jackpot_label')} #{roundNumber}</div>
          <div className="jackpot-value">
            <span className="jackpot-number">{currentJackpot.replace(/ [A-Z]+$/, '')}</span>
            <span className="jackpot-currency"> GRAM</span>
          </div>
          {depositCount > 0 && (
            <div className="jackpot-sub">
              {depositCount} {plural(depositCount, 'deposits')}
            </div>
          )}
        </section>

        <section className="card timer-card">
          {isPaused ? (
            <>
              <div className="card-label">{t('next_round_in')}</div>
              <div className="timer-value">{formatTimeLeft(nextRoundTime)}</div>
            </>
          ) : timeLeft > 0 ? (
            <>
              <div className="card-label">{t('round_ends_in')}</div>
              <div className="timer-value">{formattedTimeLeft}</div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </>
          ) : (
            <>
              <div className="card-label">{t('round_done', { n: roundNumber })}</div>
              <div className="timer-done">{t('round_done_wait')}</div>
            </>
          )}
        </section>

        {!isPaused && (
          locked ? (
            <div className="lock-block">
              <div className="personal-timer">{formatTimeLeft(lockDisplay)}</div>
              {!inviteOnCooldown && !invited && (
                <span className="invite-link" onClick={copyInvite}>
                  {t('invite_cta')}
                </span>
              )}
              {invited && (
                <div className="invite-note">{t('invite_copied')}</div>
              )}
            </div>
          ) : (
            <button
              className="deposit-btn"
              disabled={depositDisabled}
              onClick={handleDeposit}
            >
              {depositing
                ? t('sending')
                : wallet
                  ? t('deposit_btn', { amount: DEPOSIT_AMOUNT / 1e9 })
                  : <>{t('connect_wallet')}<br />{t('connect_wallet_sub')}</>}
            </button>
          )
        )}

        {(error || depositError) && (
          <div className="notice-error">{error ?? depositError}</div>
        )}

        {deposits.length > 0 && (
          <section className="card deposits-card">
            <div className="card-label">{t('round_bets')}</div>
            {deposits.slice(0, 15).map((d, i) => {
              const isMe = normalize(d.player_address) === myAddress;
              return (
                <a
                  key={`${d.deposit_time}-${i}`}
                  className="table-row"
                  href={d.tx_hash ? `${viewerBase}/transaction/${d.tx_hash}` : `${viewerBase}/${d.player_address}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <BlockiesIdenticon address={d.player_address} size={26} />
                  <span className="table-address">
                    {shortAddress(d.player_address)}
                    {isMe && ` (${t('you')})`}
                  </span>
                  <span className="table-amount">{formatGram(d.amount)}</span>
                </a>
              );
            })}
            {deposits.length > 15 && (
              <div
                className="table-row show-all-row"
                onClick={() => {
                  fetch(`${API_ENDPOINTS.DEPOSITS}?limit=200`)
                    .then((r) => (r.ok ? r.json() : []))
                    .then(setAllDeposits)
                    .catch(() => {});
                  setDepositsOpen(true);
                }}
              >
                <span className="table-address show-all-label">{t('show_all')}</span>
              </div>
            )}
          </section>
        )}

        {lastWinner && (
          <a
            className="card winner-card"
            href={`${viewerBase}/${lastWinner.winner_address}`}
            target="_blank"
            rel="noreferrer"
          >
            <div className="winner-head">
              <div className="card-label">
                {t('last_winner', { n: lastWinner.round_number })}
              </div>
              <span
                className="winners-plus"
                onClick={(e) => {
                  e.preventDefault();
                  fetch(`${API_ENDPOINTS.HISTORY}?limit=50`)
                    .then((r) => (r.ok ? r.json() : []))
                    .then(setWinners)
                    .catch(() => {});
                  setWinnersOpen(true);
                }}
              >+</span>
            </div>
            <div className="winner-row">
              <BlockiesIdenticon address={lastWinner.winner_address ?? ''} size={32} />
              <div>
                <div className="winner-address">
                  {t('round')} #{lastWinner.round_number} · {shortAddress(lastWinner.winner_address)}
                </div>
                <div className="winner-amount">{formatGram(lastWinner.winner_amount)} ↗</div>
              </div>
            </div>
          </a>
        )}

      </main>

      {depositsOpen && (
        <div className="winners-overlay" onClick={() => setDepositsOpen(false)}>
          <div className="winners-panel card" onClick={(e) => e.stopPropagation()}>
            <div className="winner-head">
              <div className="card-label">{t('round_bets')}</div>
              <span className="winners-plus" onClick={() => setDepositsOpen(false)}>×</span>
            </div>
            {allDeposits.map((d, i) => {
              const isMe = normalize(d.player_address) === myAddress;
              return (
                <a
                  key={`${d.deposit_time}-${i}`}
                  className="table-row"
                  href={d.tx_hash ? `${viewerBase}/transaction/${d.tx_hash}` : `${viewerBase}/${d.player_address}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <BlockiesIdenticon address={d.player_address} size={26} />
                  <span className="table-address">
                    {shortAddress(d.player_address)}
                    {isMe && ` (${t('you')})`}
                  </span>
                  <span className="table-amount">{formatGram(d.amount)}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {winnersOpen && (
          <div className="winners-overlay" onClick={() => setWinnersOpen(false)}>
            <div className="winners-panel card" onClick={(e) => e.stopPropagation()}>
              <div className="winner-head">
                <div className="overlay-tabs">
                  <span
                    className={overlayTab === 'winners' ? 'overlay-tab active' : 'overlay-tab'}
                    onClick={() => setOverlayTab('winners')}
                  >{t('winners_tab')}</span>
                  <span
                    className={overlayTab === 'leaders' ? 'overlay-tab active' : 'overlay-tab'}
                    onClick={() => setOverlayTab('leaders')}
                  >{t('leaders_tab')}</span>
                </div>
                <span className="winners-plus" onClick={() => setWinnersOpen(false)}>×</span>
              </div>
              {overlayTab === 'winners' && winners.map((w) => (
                <a
                  key={w.round_number}
                  className="table-row"
                  href={`${viewerBase}/${w.winner_address}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <BlockiesIdenticon address={w.winner_address ?? ''} size={26} />
                  <span className="table-address">
                    #{w.round_number} · {shortAddress(w.winner_address)}
                  </span>
                  <span className="table-amount">{formatGram(w.winner_amount)}</span>
                </a>
              ))}
              {overlayTab === 'winners' && winners.length === 0 && (
                <div className="invite-note">{t('empty')}</div>
              )}
              {overlayTab === 'leaders' && leaders.map((l, i) => (
                <a
                  key={l.address}
                  className="table-row"
                  href={`${viewerBase}/${l.address}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <BlockiesIdenticon address={l.address} size={26} />
                  <span className="table-address">
                    {i === 0 ? '🏆 ' : `${i + 1}. `}{shortAddress(l.address)}
                    {normalize(l.address) === myAddress && ` (${t('you')})`}
                  </span>
                  <span className="table-amount">
                    {l.wins} {plural(l.wins, 'wins')} · {formatGram(l.winnings)}
                  </span>
                </a>
              ))}
              {overlayTab === 'leaders' && leaders.length === 0 && (
                <div className="invite-note">{t('empty')}</div>
              )}
            </div>
          </div>
        )}

      {soundOpen && (
        <div className="winners-overlay" onClick={() => setSoundOpen(false)}>
          <div className="winners-panel card" onClick={(e) => e.stopPropagation()}>
            <div className="winner-head">
              <div className="card-label">{t('sound')}</div>
              <span className="winners-plus" onClick={() => setSoundOpen(false)}>×</span>
            </div>
            {BUILTIN_SOUNDS.map((s) => (
              <div
                key={s.id}
                className={`table-row lang-row${soundUrl === `/sounds/${s.id}.wav` ? ' active' : ''}`}
                onClick={() => pickBuiltin(s.id)}
              >
                <span className="table-address">{s.name}</span>
                <span className="table-amount">{soundUrl === `/sounds/${s.id}.wav` ? '✓' : ''}</span>
              </div>
            ))}
            <div
              className={`table-row lang-row${soundUrl === customUrl ? ' active' : ''}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <span className="table-address">{t('sound_pick_file')}</span>
              <span className="table-amount">{soundUrl === customUrl ? '✓' : ''}</span>
            </div>
          </div>
        </div>
      )}

      {langOpen && (
        <div className="winners-overlay" onClick={() => setLangOpen(false)}>
          <div className="winners-panel card" onClick={(e) => e.stopPropagation()}>
            <div className="winner-head">
              <div className="card-label">🌐</div>
              <span className="winners-plus" onClick={() => setLangOpen(false)}>×</span>
            </div>
            {(Object.keys(LANG_NAMES) as Lang[]).map((code) => (
              <div
                key={code}
                className={`table-row lang-row${code === lang ? ' active' : ''}`}
                onClick={() => { setLang(code); window.location.reload(); }}
              >
                <span className="table-address">{LANG_NAMES[code]}</span>
                <span className="table-amount">{code === lang ? '✓' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {rulesOpen && (
        <div className="winners-overlay" onClick={closeRules}>
          <div className="winners-panel card rules-panel" onClick={(e) => e.stopPropagation()}>
            <div className="winner-head">
              <div className="card-label">{t('rules_title')}</div>
              <span className="winners-plus" onClick={closeRules}>×</span>
            </div>
            <div className="rules-label">{t('rules_h1')}</div>
            <ul className="rules-list">
              <li>{t('rules_1', { amount: DEPOSIT_AMOUNT / 1e9 })}</li>
              <li>{t('rules_2')}</li>
              <li>{t('rules_3')}</li>
              <li>{t('rules_4')}</li>
              <li>{t('rules_5')}</li>
              <li>{t('rules_6')}</li>
              <li>{t('rules_7')}</li>
            </ul>
            <div className="rules-label">{t('rules_h2')}</div>
            <ul className="rules-list">
              <li>{t('safe_1')}</li>
              <li>{t('safe_2')}</li>
              <li>{t('safe_3')}</li>
              <li>{t('safe_4')}</li>
              <li>{t('safe_5')}{' '}
                {CONTRACT_ADDRESS && (
                  <a className="rules-link" href={`${viewerBase}/${CONTRACT_ADDRESS}`} target="_blank" rel="noreferrer">Tonviewer ↗</a>
                )}
              </li>
              <li>{t('safe_6')}</li>
              <li>{t('safe_7')}</li>
            </ul>
          </div>
        </div>
      )}

      {preLaunch && (
        <div className="splash-overlay">
          <div className="splash-card">
            <div className="splash-logo">◆</div>
            <div className="splash-title">kiloGRAM</div>
            <div className="splash-sub">{t('splash_tag')}</div>
            <div className="splash-label">{t('splash_in')}</div>
            <div className="splash-timer">{formatTimeLeft(launchLeft)}</div>
            <div className="splash-connect">
              <button className="splash-connect-btn" onClick={() => tonConnectUI.openModal()}>
                {t('splash_connect')}
              </button>
              <div className="splash-note">
                {t('splash_note')}<br />{t('splash_note2')}
              </div>
            </div>
            {BOT_LINK && (
              <a className="splash-bot" href={BOT_LINK} target="_blank" rel="noreferrer">
                {t('splash_bot')}
              </a>
            )}
          </div>
        </div>
      )}

      {isLoading && !round && (
        <div className="loading-overlay">
          <div className="loading-text">{t('loading')}</div>
        </div>
      )}
    </div>
  );
};

export default Game;
